import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import dayjs from 'dayjs';
import { AzureCostBreakdown, AzureCostDashboard } from '../../apiClients/SupportClient';
import useViewportMode from '../../hooks/useViewportMode';

interface AzureCostsViewProps {
    dashboard?: AzureCostDashboard;
    period: string;
    onPeriodChange: (period: string) => void;
}

const formatMoney = (value: number, currency: string) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: value < 100 ? 2 : 0,
    maximumFractionDigits: value < 100 ? 2 : 0,
}).format(value || 0);

const AzureCostsView = ({ dashboard, period, onPeriodChange }: AzureCostsViewProps) => {
    const { mode, isPhone, isTablet, isLandscape, isDesktop } = useViewportMode();
    if (!dashboard)
        return <Alert severity="info">Azure cost data is loading.</Alert>;

    const currency = dashboard.Currency || 'USD';
    const changeLabel = dashboard.ChangePercent === undefined || dashboard.ChangePercent === null
        ? 'No prior-period baseline'
        : `${dashboard.ChangePercent >= 0 ? '+' : ''}${dashboard.ChangePercent.toFixed(1)}% versus ${dashboard.PreviousPeriodLabel}`;
    const projectionLabel = dashboard.ProjectionSource === 'AzureForecast'
        ? 'Azure month-end forecast'
        : dashboard.ProjectionSource === 'RunRate'
            ? 'Projected from current daily run rate'
            : 'Final period total';
    const metricColumns = isPhone ? 12 : isTablet ? 6 : 3;
    const splitColumns = isTablet && !isLandscape ? 12 : 6;

    return <Stack spacing={2.5} data-viewport-mode={mode}>
        <Stack direction={isPhone ? 'column' : 'row'} gap={1.5} justifyContent="space-between" alignItems={isPhone ? 'stretch' : 'center'}>
            <Box>
                <Typography variant="h5">Azure spend</Typography>
                <Typography variant="body2" color="text.secondary">
                    Cost data through {dayjs(dashboard.DataThroughDate).format('MMMM D, YYYY')} · refreshed {dayjs(dashboard.RetrievedAt).format('MMM D, h:mm A')}
                </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: isPhone ? 0 : 210, '& .MuiInputBase-root': { minHeight: isDesktop ? undefined : 44 } }}>
                <InputLabel id="azure-cost-period-label">Cost period</InputLabel>
                <Select labelId="azure-cost-period-label" label="Cost period" value={period || dashboard.Period} onChange={event => onPeriodChange(event.target.value)}>
                    {dashboard.AvailablePeriods.map(value => <MenuItem key={value} value={value}>{dayjs(`${value}-01`).format('MMMM YYYY')}</MenuItem>)}
                </Select>
            </FormControl>
        </Stack>

        {dashboard.IsStale && <Alert severity="warning">Azure is temporarily unavailable. Showing the most recent cached cost snapshot.</Alert>}
        {dashboard.IsEstimate && <Alert severity="info">Open-month Azure charges are estimated and can be delayed or rerated as usage settles.</Alert>}

        <Grid container spacing={1.5}>
            <CostMetricCard columns={metricColumns} label={dashboard.IsEstimate ? 'Month to date' : 'Period total'} value={formatMoney(dashboard.ActualCost, currency)} helper={changeLabel} icon={<CalendarMonthRoundedIcon />} color="#2879d0" />
            <CostMetricCard columns={metricColumns} label={dashboard.IsEstimate ? 'Projected month end' : 'Final period'} value={formatMoney(dashboard.ProjectedMonthEndCost, currency)} helper={projectionLabel} icon={<AutoGraphRoundedIcon />} color="#7b4bc4" />
            <CostMetricCard columns={metricColumns} label="Average daily cost" value={formatMoney(dashboard.AverageDailyCost, currency)} helper={`${dashboard.DailyCosts.length} usage days reported`} icon={<SpeedRoundedIcon />} color="#da7b1e" />
            <CostMetricCard columns={metricColumns} label={dashboard.PreviousPeriodLabel} value={formatMoney(dashboard.PreviousPeriodCost, currency)} helper={`${formatMoney(Math.abs(dashboard.ChangeAmount), currency)} ${dashboard.ChangeAmount >= 0 ? 'higher' : 'lower'} this period`} icon={<CloudQueueRoundedIcon />} color="#178d69" />
        </Grid>

        <Card variant="outlined">
            <CardContent>
                <Typography variant="h5">Daily cost trend</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Actual reported charges by usage date.</Typography>
                <DailyCostChart dashboard={dashboard} />
            </CardContent>
        </Card>

        <Grid container spacing={2}>
            <Grid item xs={12}>
                <BreakdownCard title="Subscriptions" subtitle="Combined spend with each Azure subscription shown separately." items={dashboard.SubscriptionCosts || []} currency={currency} />
            </Grid>
            <Grid item xs={12} sm={splitColumns} lg={6}>
                <BreakdownCard title="Management categories" subtitle="A simplified operating view of where Azure spend is going." items={dashboard.CategoryCosts} currency={currency} />
            </Grid>
            <Grid item xs={12} sm={splitColumns} lg={6}>
                <BreakdownCard title="Azure services" subtitle="The service names reported directly by Azure Cost Management." items={dashboard.ServiceCosts} currency={currency} />
            </Grid>
        </Grid>

        <Card variant="outlined">
            <CardContent>
                <Typography variant="h5">Highest-cost resources</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Raw Azure resources underlying the category and service totals.</Typography>
                <Grid container spacing={1.25}>
                    {dashboard.ResourceCosts.map(resource => <Grid item xs={12} md={isDesktop ? 6 : 12} key={`${resource.SubscriptionId}:${resource.ResourceId}`}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>{resource.Name}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{resource.ResourceType}</Typography>
                                    </Box>
                                    <Typography fontWeight={900} whiteSpace="nowrap">{formatMoney(resource.Cost, currency)}</Typography>
                                </Stack>
                                <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.25 }}>
                                    <Chip size="small" label={resource.Category} color="primary" variant="outlined" />
                                    {resource.SubscriptionName && <Chip size="small" label={resource.SubscriptionName} variant="outlined" />}
                                    <Chip size="small" label={resource.ServiceName} variant="outlined" />
                                    <Chip size="small" label={resource.ResourceGroup} variant="outlined" />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>)}
                    {dashboard.ResourceCosts.length === 0 && <Grid item xs={12}><Alert severity="info">Azure did not attribute costs to individual resources for this period.</Alert></Grid>}
                </Grid>
            </CardContent>
        </Card>
    </Stack>;
};

const CostMetricCard = ({ columns, label, value, helper, icon, color }: {
    columns: number;
    label: string;
    value: string;
    helper: string;
    icon: React.ReactNode;
    color: string;
}) => <Grid item xs={12} sm={columns} lg={columns}>
    <Card variant="outlined" sx={{ height: '100%', borderTop: `4px solid ${color}` }}>
        <CardContent>
            <Stack direction="row" gap={1} alignItems="center" color={color}>{icon}<Typography variant="overline">{label}</Typography></Stack>
            <Typography variant="h3" sx={{ my: 0.5 }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{helper}</Typography>
        </CardContent>
    </Card>
</Grid>;

const DailyCostChart = ({ dashboard }: { dashboard: AzureCostDashboard }) => {
    const max = Math.max(...dashboard.DailyCosts.map(item => item.Cost), 0.01);
    return <Box role="img" aria-label={`Daily Azure cost trend for ${dashboard.PeriodLabel}`}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 180, borderBottom: 1, borderColor: 'divider', overflowX: 'auto', pb: 0.5 }}>
            {dashboard.DailyCosts.map(item => <Box key={item.Date} title={`${dayjs(item.Date).format('MMM D')}: ${formatMoney(item.Cost, dashboard.Currency)}`} sx={{ flex: '1 0 10px', minWidth: 10, maxWidth: 34, height: `${Math.max(2, item.Cost / max * 100)}%`, bgcolor: 'primary.main', borderRadius: '4px 4px 0 0' }} />)}
        </Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">{dashboard.DailyCosts[0] ? dayjs(dashboard.DailyCosts[0].Date).format('MMM D') : 'No usage'}</Typography>
            <Typography variant="caption" color="text.secondary">{dashboard.DailyCosts.length ? dayjs(dashboard.DailyCosts[dashboard.DailyCosts.length - 1].Date).format('MMM D') : ''}</Typography>
        </Stack>
    </Box>;
};

const BreakdownCard = ({ title, subtitle, items, currency }: { title: string; subtitle: string; items: AzureCostBreakdown[]; currency: string }) => <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>
        <Stack spacing={1.5}>
            {items.map(item => <Box key={item.Name}>
                <Stack direction="row" justifyContent="space-between" gap={1}><Typography fontWeight={700}>{item.Name}</Typography><Typography fontWeight={800}>{formatMoney(item.Cost, currency)}</Typography></Stack>
                <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, item.Percent))} sx={{ mt: 0.5, height: 7, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">{item.Percent.toFixed(1)}% of reported spend</Typography>
            </Box>)}
            {items.length === 0 && <Typography color="text.secondary">No reported cost for this period.</Typography>}
        </Stack>
    </CardContent>
</Card>;

export default AzureCostsView;
